/**
 * Host SDK for the demo casino RGS.
 *
 * Demo credits only — no cash value. Amounts are decimal-integer strings in
 * wallet units (100 units = 1 demo credit).
 *
 *   const casino = new CasinoClient({ baseUrl: 'https://casino.example' });
 *   const launch = await casino.launchGame({ gameId: 'g01-count-accumulator', container, mode: 'iframe' });
 *   const off = casino.subscribeToEvents({ onBalance: (b) => render(b) });
 */
import type { BalanceDto, ErrorCode, GameListItem, LaunchResult, RoundDto, SessionStateDto, WalletTxDto } from '@casino/core';

export class CasinoApiError extends Error {
  constructor(
    readonly code: ErrorCode | 'NETWORK',
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'CasinoApiError';
  }
}

export interface CasinoClientOptions {
  /** API origin; defaults to same origin. */
  baseUrl?: string;
  /** Cookie-session CSRF token (host app). Obtained from GET /v1/me. */
  csrfToken?: string | (() => string | null);
  /** Game-scoped bearer token from a launch-code exchange (embedded games). Keep in memory only. */
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}

export function newIdempotencyKey(prefix = 'k'): string {
  const rnd = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${rnd}`;
}

export class CasinoClient {
  private readonly base: string;
  private readonly f: typeof fetch;
  constructor(private opts: CasinoClientOptions = {}) {
    this.base = (opts.baseUrl ?? '').replace(/\/$/, '');
    this.f = opts.fetchImpl ?? ((...a) => fetch(...a));
  }

  setBearerToken(token: string | null): void {
    this.opts.bearerToken = token;
  }

  setCsrfToken(token: string | null): void {
    this.opts.csrfToken = token ?? undefined;
  }

  async request<T>(method: string, path: string, body?: unknown, headers: Record<string, string> = {}): Promise<T> {
    const h: Record<string, string> = { accept: 'application/json', ...headers };
    if (body !== undefined) h['content-type'] = 'application/json';
    const csrf = typeof this.opts.csrfToken === 'function' ? this.opts.csrfToken() : this.opts.csrfToken;
    if (csrf && method !== 'GET') h['x-csrf-token'] = csrf;
    if (this.opts.bearerToken) h.authorization = `Bearer ${this.opts.bearerToken}`;
    let res: Response;
    try {
      res = await this.f(`${this.base}${path}`, { method, headers: h, body: body === undefined ? undefined : JSON.stringify(body), credentials: this.opts.bearerToken ? 'omit' : 'include' });
    } catch (e) {
      throw new CasinoApiError('NETWORK', (e as Error).message || 'network error', 0);
    }
    const text = await res.text();
    const json = text ? JSON.parse(text) : {};
    if (!res.ok) {
      const err = json?.error ?? {};
      throw new CasinoApiError(err.code ?? 'INTERNAL', err.message ?? res.statusText, res.status, err.details);
    }
    return json as T;
  }

  getBalance(namespace: 'main' | 'presentation' | 'free' | 'controlled' = 'main'): Promise<BalanceDto & { label: string }> {
    return this.request('GET', `/v1/wallet/balance?namespace=${namespace}`);
  }

  /** The player's own recent wallet operations (debits, credits, grants…), newest first. */
  getWalletActivity(opts: { namespace?: 'main' | 'presentation' | 'free' | 'controlled'; limit?: number } = {}): Promise<{ transactions: WalletTxDto[] }> {
    return this.request('GET', `/v1/wallet/transactions?namespace=${opts.namespace ?? 'main'}&limit=${opts.limit ?? 30}`);
  }

  listGames(): Promise<{ games: GameListItem[] }> {
    return this.request('GET', '/v1/games');
  }

  /**
   * Launch a game. The server resolves the tenant's skin assignment; an
   * optional skin choice is honoured only for authorised editors.
   * - mode 'direct': returns the launch data; the host renders /play itself.
   * - mode 'iframe': appends an iframe that exchanges the one-time launch code
   *   for an in-memory, session-scoped token (works without third-party cookies).
   */
  async launchGame(input: {
    /** Title to launch (preferred). */
    titleId?: string;
    /** Engine id: launches the operator's default title for that engine. */
    gameId?: string;
    mode?: 'direct' | 'iframe';
    container?: HTMLElement;
    play?: 'random' | 'presentation' | 'controlled';
    skinId?: string;
    skinVersion?: number;
    onMessage?: (m: GameFrameMessage) => void;
  }): Promise<LaunchResult & { frame?: GameFrame }> {
    const launch = await this.request<LaunchResult>('POST', '/v1/game-sessions', {
      gameId: input.titleId ? undefined : input.gameId,
      titleId: input.titleId,
      mode: input.play,
      skinId: input.skinId,
      skinVersion: input.skinVersion,
    });
    if (input.mode === 'iframe') {
      if (!input.container) throw new Error('container is required for iframe mode');
      const frame = createGameFrame(input.container, { origin: this.base || location.origin, gameId: launch.titleId ?? launch.gameId, launchCode: launch.launchCode, onMessage: input.onMessage });
      return { ...launch, frame };
    }
    return launch;
  }

  getSessionState(sessionId: string): Promise<SessionStateDto> {
    return this.request('GET', `/v1/game-sessions/${sessionId}/state`);
  }

  /** Place a round. Retries transport failures with the SAME idempotency key. */
  /**
   * `stake` is the BASE bet; `options` are the game's round options (see session state `roundOptions`),
   * e.g. { ante: true }. The wallet is debited `charge` = base bet x option cost (returned on the round).
   */
  async placeRound(
    sessionId: string,
    stake: string,
    opts: { idempotencyKey?: string; retries?: number; scenario?: string; options?: Record<string, boolean | string> } = {},
  ): Promise<RoundDto & { pending?: { code: string; message: string } }> {
    const key = opts.idempotencyKey ?? newIdempotencyKey('round');
    let last: unknown;
    for (let i = 0; i <= (opts.retries ?? 2); i++) {
      try {
        return await this.request('POST', `/v1/game-sessions/${sessionId}/rounds`, { stake, ...(opts.scenario ? { scenario: opts.scenario } : {}), ...(opts.options && Object.keys(opts.options).length ? { options: opts.options } : {}) }, { 'idempotency-key': key });
      } catch (e) {
        last = e;
        if (!(e instanceof CasinoApiError) || e.code !== 'NETWORK') throw e;
        await new Promise((r) => setTimeout(r, 400 * (i + 1)));
      }
    }
    throw last;
  }

  /**
   * Answer the decision a paused round waits for (round.state === 'AWAITING_ACTION', see round.pendingAction),
   * e.g. `{ actionId: 'g14.gamble', choice: 'gamble', step: round.pendingAction.step }`. Transport failures are
   * retried with the SAME idempotency key, so a decision is never applied twice. The response is the round with
   * every event so far (and the next pendingAction, or the settled outcome).
   */
  async actOnRound(
    sessionId: string,
    roundId: string,
    action: { actionId: string; choice: string; step?: number },
    opts: { idempotencyKey?: string; retries?: number } = {},
  ): Promise<RoundDto> {
    const key = opts.idempotencyKey ?? newIdempotencyKey('act');
    let last: unknown;
    for (let i = 0; i <= (opts.retries ?? 2); i++) {
      try {
        return await this.request('POST', `/v1/sessions/${sessionId}/rounds/${roundId}/actions`, action, { 'idempotency-key': key });
      } catch (e) {
        last = e;
        if (!(e instanceof CasinoApiError) || e.code !== 'NETWORK') throw e;
        await new Promise((r) => setTimeout(r, 400 * (i + 1)));
      }
    }
    throw last;
  }

  /** Re-drive / fetch a round after a reconnect (never re-debits). */
  resumeRound(roundId: string): Promise<RoundDto> {
    return this.request('POST', `/v1/rounds/${roundId}/actions`, { type: 'resume' });
  }

  getRound(roundId: string): Promise<RoundDto> {
    return this.request('GET', `/v1/rounds/${roundId}`);
  }

  getReplay(roundId: string): Promise<{ round: RoundDto; skin: { skinId: string; version: number; hash: string; manifest: unknown }; rules: unknown; replay: true }> {
    return this.request('GET', `/v1/rounds/${roundId}/replay`);
  }

  getRoundHistory(opts: { gameId?: string; sessionId?: string; limit?: number } = {}): Promise<{ rounds: RoundDto[] }> {
    const q = new URLSearchParams();
    if (opts.gameId) q.set('gameId', opts.gameId);
    if (opts.sessionId) q.set('sessionId', opts.sessionId);
    q.set('limit', String(opts.limit ?? 20));
    return this.request('GET', `/v1/rounds?${q}`);
  }

  async closeGame(sessionId: string, frame?: GameFrame): Promise<void> {
    frame?.destroy();
    await this.request('POST', `/v1/game-sessions/${sessionId}/close`, {});
  }

  /**
   * Server-sent balance/round events with reconnect. Balance updates carry the
   * wallet version; older or duplicate versions are ignored.
   */
  subscribeToEvents(handlers: { onBalance?: (b: { balance: string; version: string; namespace: string }) => void; onRound?: (r: unknown) => void; onError?: (e: Event) => void }): () => void {
    if (this.opts.bearerToken) throw new Error('SSE uses the host cookie session; embedded games receive balance updates via postMessage');
    const es = new EventSource(`${this.base}/v1/events`, { withCredentials: true });
    const latest = new Map<string, bigint>();
    es.addEventListener('balance', (ev) => {
      const d = JSON.parse((ev as MessageEvent).data);
      const v = BigInt(d.version);
      const prev = latest.get(d.namespace) ?? -1n;
      if (v <= prev) return;
      latest.set(d.namespace, v);
      handlers.onBalance?.(d);
    });
    es.addEventListener('round', (ev) => handlers.onRound?.(JSON.parse((ev as MessageEvent).data)));
    es.onerror = (e) => handlers.onError?.(e);
    return () => es.close();
  }
}

// ---------------------------------------------------------------------------------- iframe bridge
export type GameFrameMessage =
  | { type: 'casino:ready'; sessionId: string }
  | { type: 'casino:balance'; balance: string; version: string }
  /** a finished round; table rounds also carry the player's total stake on it */
  | { type: 'casino:round'; roundId: string; state: string; payout?: string; staked?: string }
  | { type: 'casino:decision'; roundId: string; actionId: string; step: number }
  | { type: 'casino:close' }
  | { type: 'casino:error'; code: string; message: string }
  /** acknowledgements of the host's pause / resume */
  | { type: 'casino:paused' }
  | { type: 'casino:resumed' }
  /** the game's sound is on / off: sent once when ready, and whenever it changes (in the game or by the host) */
  | { type: 'casino:sound'; on: boolean };

/** Messages the host may send INTO the game frame. */
export type HostFrameMessage =
  | { type: 'casino:pause'; message?: string }
  | { type: 'casino:resume' }
  /** turn the game's sound on / off (answered with the game's casino:sound) */
  | { type: 'casino:sound'; on: boolean }
  /** open the game's rules and paytable */
  | { type: 'casino:rules' };

export interface GameFrame {
  iframe: HTMLIFrameElement;
  /** Pause play (e.g. a responsible-gambling reality check): autoplay stops, the current round finishes, no new round starts. */
  pause(message?: string): void;
  resume(): void;
  /** Sound on / off, e.g. from the host page's game bar. */
  setSound(on: boolean): void;
  /** Open the game's rules and paytable. */
  openRules(): void;
  destroy(): void;
}

/**
 * Mounts the game in an iframe. The one-time launch code is passed in the URL
 * FRAGMENT (never sent to servers or logs) and exchanged inside the frame.
 * Messages are accepted only when BOTH event.origin matches the game origin
 * AND event.source is this iframe's window.
 */
export function createGameFrame(container: HTMLElement, o: { origin: string; gameId: string; launchCode: string; onMessage?: (m: GameFrameMessage) => void }): GameFrame {
  const iframe = document.createElement('iframe');
  iframe.src = `${o.origin}/embed/${encodeURIComponent(o.gameId)}#code=${encodeURIComponent(o.launchCode)}&parent=${encodeURIComponent(location.origin)}`;
  iframe.allow = 'autoplay; fullscreen';
  iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-popups');
  iframe.style.cssText = 'border:0;width:100%;height:100%;display:block;background:#000';
  iframe.title = 'Casino game';
  container.appendChild(iframe);
  const expectedOrigin = new URL(o.origin).origin;
  const listener = (ev: MessageEvent) => {
    if (ev.origin !== expectedOrigin || ev.source !== iframe.contentWindow) return;
    const m = ev.data as GameFrameMessage;
    if (!m || typeof m !== 'object' || typeof (m as any).type !== 'string' || !(m as any).type.startsWith('casino:')) return;
    o.onMessage?.(m);
  };
  window.addEventListener('message', listener);
  const send = (m: HostFrameMessage) => iframe.contentWindow?.postMessage(m, expectedOrigin);
  return {
    iframe,
    pause(message?: string) {
      send({ type: 'casino:pause', message });
    },
    resume() {
      send({ type: 'casino:resume' });
    },
    setSound(on: boolean) {
      send({ type: 'casino:sound', on });
    },
    openRules() {
      send({ type: 'casino:rules' });
    },
    destroy() {
      window.removeEventListener('message', listener);
      iframe.remove();
    },
  };
}

// Table games (tg01-tg10): see ./tables.ts. Node-only webhook verification lives in ./tables-node.ts.
export { TableClient, type TableClientOptions, type CommandResult, type TableEventMessage } from './tables.js';
